import { useEffect, useState } from 'react';

/**
 * When animation needs to get activated after section comes into the viewport
 * @param {null} root - defaults to the browser viewport || null
 * @param {string} rootMargin - css margin around the root (px or %)
 * @param {number|array} threshold - 1.0 means that when 100% of the target is visible, the callback is invoked
 * @param {boolean} unobserve - when true, animation will be activated only once (first time)
 * */

export function useIntersectionObserver(
  target,
  options = { root: null, rootMargin: '0px', threshold: 1.0, unobserve: false }
) {
  const [isIntersecting, setIntersecting] = useState(false);

  const callback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setIntersecting(true);
      } else {
        setIntersecting(false);
      }
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callback, {
      threshold: options?.threshold,
      rootMargin: options?.rootMargin,
    });

    observer.observe(target?.current);

    if (isIntersecting && options.unobserve) {
      observer.unobserve(target.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [target, options, isIntersecting]);

  return isIntersecting;
}
